import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';

const GOAL_OPTIONS = [
  "Move my body more",
  "Decrease stress",
  "Manage busy lifestyle",
  "Feel less anxious",
  "Feel less depressed",
  "Feel more motivated and confident",
  "Slow down",
  "Process old wounds"
];

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

const ConsultationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    state: '',
    goals: [],
    additional_info: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGoalToggle = (goal) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.includes(goal)
        ? prev.goals.filter(g => g !== goal)
        : [...prev.goals, goal]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consultation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setSubmitStatus('success');
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '', phone: '', email: '', city: '', state: '', goals: [], additional_info: ''
        });
        setSubmitStatus(null);
        onClose();
      }, 3000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="consultation-modal-overlay"
    >
      <div 
        className="relative bg-[#FCF9F5] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="consultation-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 transition-colors z-10"
          data-testid="consultation-close-btn"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="px-8 pt-8 pb-4 border-b border-[#D5D2C5]">
          <h2 className="text-3xl serif font-light text-gray-900">Begin Your Journey</h2>
          <p className="text-gray-600 mt-2">Complete this form and I will reach out to schedule your consultation.</p>
        </div>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className="mx-8 mt-6 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="success-message">
            <p className="text-green-800 font-medium">Thank you for reaching out!</p>
            <p className="text-green-700 text-sm mt-1">I will be in touch soon to schedule your consultation.</p>
          </div>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <div className="mx-8 mt-6 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="error-message">
            <p className="text-red-800 font-medium">Something went wrong.</p>
            <p className="text-red-700 text-sm mt-1">Please try again or email jessica@heisewellness.com directly.</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6" data-testid="consultation-form">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-purple-600">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Your name"
              data-testid="input-name"
            />
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number <span className="text-purple-600">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="(555) 555-5555"
              data-testid="input-phone"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address <span className="text-purple-600">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="you@example.com"
              data-testid="input-email"
            />
          </div>

          {/* City & State */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                City <span className="text-purple-600">*</span>
              </label>
              <input
                type="text"
                id="city"
                name="city"
                required
                value={formData.city}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="Your city"
                data-testid="input-city"
              />
            </div>
            <div>
              <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                State <span className="text-purple-600">*</span>
              </label>
              <select
                id="state"
                name="state"
                required
                value={formData.state}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                data-testid="input-state"
              >
                <option value="">Select state</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Goals Checkboxes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              What brings you here? <span className="text-gray-500">(Select all that apply)</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {GOAL_OPTIONS.map((goal) => (
                <label
                  key={goal}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                    formData.goals.includes(goal)
                      ? 'bg-purple-50 border-purple-300 text-purple-800'
                      : 'bg-white border-gray-200 hover:border-purple-200'
                  }`}
                  data-testid={`goal-${goal.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <input
                    type="checkbox"
                    checked={formData.goals.includes(goal)}
                    onChange={() => handleGoalToggle(goal)}
                    className="sr-only"
                  />
                  <span className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 flex-shrink-0 ${
                    formData.goals.includes(goal) 
                      ? 'bg-purple-600 border-purple-600' 
                      : 'border-gray-300'
                  }`}>
                    {formData.goals.includes(goal) && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span className="text-sm">{goal}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Info */}
          <div>
            <label htmlFor="additional_info" className="block text-sm font-medium text-gray-700 mb-1">
              Briefly describe your goals <span className="text-gray-500">(Optional)</span>
            </label>
            <textarea
              id="additional_info"
              name="additional_info"
              rows={4}
              value={formData.additional_info}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
              placeholder="Share anything you'd like me to know before our consultation..."
              data-testid="input-additional-info"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || submitStatus === 'success'}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-8 py-4 rounded-lg text-lg font-medium transition-all duration-300 flex items-center justify-center"
            data-testid="submit-consultation-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin mr-2" size={20} />
                Submitting...
              </>
            ) : submitStatus === 'success' ? (
              'Submitted!'
            ) : (
              'Submit Inquiry'
            )}
          </button>

          <p className="text-center text-sm text-gray-500">
            All inquiries are confidential.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ConsultationModal;
